<template>
  <div class="min-w-0 w-full">
    <vf-page-loader v-if="isShopPayLoading" class="!z-above-header" />
    <template v-if="shouldRender">
      <vf-button
        v-if="showExpressCheckoutButton"
        class="w-full"
        data-test-id="cart-express-checkout-button"
        :disabled="!cart.totalItems"
        variant="secondary"
        @click="isExpressCheckoutExpanded = true"
      >
        {{ $t.expressCheckout }}
      </vf-button>
      <div
        v-else
        class="min-w-0 w-full"
        :class="[baseClassesContainer, classContainer]"
      >
        <vf-button
          v-if="isApplePayEnabled"
          :aria-label="$t.applePayIconText"
          :class="[
            classApplePay || [
              'w-full',
              { 'h-full': isPaypalButtonEnabled },
            ],
            { 'min-w-30 basis-30 grow o:px-4': useWrappingLayout },
            applePayButton.brandClasses?.button,
          ]"
          :loading="applePayLoading"
          :size="applePayButton.size"
          :style="{
            height: matchPaypalHeight && isPaypalButtonEnabled
              ? pxToRem(paypalConfig.button.style?.height || 48) : null,
          }"
          :variant="applePayButton.variant"
          @click="openTermsAcceptanceDialog"
        >
          <vf-icon v-bind="applePayButton.icon" pos="center" :src="logos.applePay" />
        </vf-button>
        <!-- PayPal's SDK needs at least 150px for the configured button height. -->
        <form-paypal-adyen
          v-if="isPaypalReady"
          :class="[classPaypalForm || 'w-full', { 'min-w-37.5 o:w-30 shrink-0 grow': useWrappingLayout }]"
          :context="paypalContext || (context === 'quickshop' ? 'paypalPdp' : undefined)"
          :hide-pay-later-text
          :instance
          :is-express-flow
          is-same-as-shipping
          :selected-variant
        />
        <div
          v-if="isShopPayEnabled"
          :id="shopPayButtonId"
          class="h-12 w-full overflow-hidden focus-within:outline-offset-2 focus-within:outline-auto"
          :class="{ 'min-w-30 o:w-30 shrink-0 grow': useWrappingLayout }"
          data-test-id="shop-pay-button"
          style="--shop-pay-button-width: 100%; --shop-pay-button-height: 3rem"
          :style="`--shop-pay-button-border-radius: ${paypalConfig.button.style?.borderRadius ?? 2}px`"
        />
      </div>
    </template>
  </div>
  <dialog-terms-acceptance
    class-content="lg:w-1/3 py-2 px-4"
    :logo="logos.applePay"
    @initiate-apple-pay="handleApplePayCheckout"
  />
</template>

<script lang="ts" setup>
import type { ProductVariants } from '#root/api/clients/product/data-contracts'
import type { ApplePayInitPayload } from '#types/applePay'
import type { CartStoreContext } from '#types/cart'
import type { CSSClass } from '#types/common'
import type { PaypalInstance } from '#types/paypal'

interface Props {
  /** Context for Apple Pay initialization and session management */
  applePayContext?: CartStoreContext
  /** Payload data to initialize Apple Pay session */
  applePayPayload?: ApplePayInitPayload
  /** Classes to apply to the Apple Pay button */
  classApplePay?: CSSClass
  /** Classes to apply to the container element */
  classContainer?: CSSClass
  /** Classes to apply to the PayPal form */
  classPaypalForm?: CSSClass
  /** Context where the component is being used */
  context?: 'cart' | 'checkout' | 'minicart' | 'quickshop'
  /** Whether to match Apple Pay button height with PayPal button height */
  matchPaypalHeight?: boolean
  /** Context for PayPal initialization and session management */
  paypalContext?: CartStoreContext
  /** Whether to hide the "Pay Later" text in the PayPal button */
  hidePayLaterText?: boolean
  /** PayPal instance for handling payment operations */
  paypalInstance?: PaypalInstance
  /** Whether PayPal is being used in an express checkout flow */
  isExpressFlow?: boolean
  /** Selected product variant for Quickshop context */
  selectedVariant?: ProductVariants[number]
  /** Whether to show loading state on Apple Pay button */
  showApplePayLoading?: boolean
  /** Whether to show the express checkout expander button */
  showExpander?: boolean
}

const {
  applePayContext,
  applePayPayload,
  showExpander,
  paypalInstance,
  selectedVariant,
  showApplePayLoading,
  context = 'cart',
  isExpressFlow = true,
  matchPaypalHeight = true
} = defineProps<Props>()

const cart = useCartStore()
const auth = useAuthStore()
const user = useUserStore()
const config = useAppConfig()
const paypalConfig = config.components.paypal
const { logos } = config.assets.payment
const { applePayButton } = config.components.checkout
const { showApplePayTermsDialog } = config.pages.checkout

const { $deviceFingerPrint, $feature, $gtm, $viewport } = useNuxtApp()
const {
  allowApplePayOrderOnCart,
  allowApplePayOrderOnCartWithBopis,
  allowApplePayOrderOnPDP,
  allowPayPalOrderOnCart,
  allowPayPalOrderOnCartWithBopis,
  showExpressCheckoutButtonExpander,
  showExpressPaymentCtasInMinicart,
  showExpressPaymentCtasInQuickshop
} = useExpressPaymentFeatures()

const {
  initApplePaySession,
  isApplePayActive
} = useApplePay(applePayContext || (context === 'quickshop' ? 'applePayPdp' : undefined))
const { loadPaypal } = useAdyen()
const {
  destroy: destroyShopPay,
  initialize: initializeShopPay,
  isEligible: isShopPayEnabled,
  isLoading: isShopPayLoading
} = useShopPay(context)

const { DialogTermsAcceptance } = useDialogsStore()

const applePaySessionAvailable = ref(false)

const instance = ref<PaypalInstance | undefined>(paypalInstance)

watch(() => paypalInstance, (value) => instance.value = value)

const isExpressCheckoutExpanded = ref(!showExpressCheckoutButtonExpander.value)

const allowQuickshopCtas = computed(() =>
  context !== 'quickshop' || (!!selectedVariant && showExpressPaymentCtasInQuickshop.value)
)

const showPayPalPayLater = computed(() => $feature.showPayPalPayLater && !auth.isEmployee && cart.totals.total)

const isApplePayEnabled = computed(() => {
  if (!applePaySessionAvailable.value) return false

  if (context === 'quickshop')
    return allowQuickshopCtas.value && allowApplePayOrderOnPDP.value

  const baseCartRules = !cart.hasCustomsProduct
    && allowApplePayOrderOnCart.value
    && cart.totalItems
    && !auth.inStoreEmployeeLoggedIn
    && (allowApplePayOrderOnCartWithBopis.value || (!cart.hasCollectionPointItems && !cart.hasPickupItems))

  if (context === 'minicart')
    return showExpressPaymentCtasInMinicart.value && baseCartRules

  return baseCartRules
})

const isPaypalButtonEnabled = computed(() => {
  if (context === 'quickshop')
    return showExpressPaymentCtasInQuickshop.value && allowPayPalOrderOnCart.value && !auth.inStoreEmployeeLoggedIn

  const baseCartRules = allowPayPalOrderOnCart.value
    && cart.totalItems
    && !auth.inStoreEmployeeLoggedIn
    && (allowPayPalOrderOnCartWithBopis.value || (!cart.hasCollectionPointItems && !cart.hasPickupItems))

  if (context === 'minicart')
    return showExpressPaymentCtasInMinicart.value && baseCartRules

  return baseCartRules
})

const isPaypalReady = computed(() => isPaypalButtonEnabled.value && !!instance.value)

const shopPayButtonId = `shop-pay-payment-request-button-${context}`
const shopPayInitialized = ref(false)

const useWrappingLayout = computed(() => context !== 'quickshop' && !showPayPalPayLater.value)

const shouldRender = computed(() =>
  allowQuickshopCtas.value
  && (isApplePayEnabled.value || isPaypalReady.value || isShopPayEnabled.value)
  && !(context === 'cart' && user.creditedConsumerProfileType)
)

const showExpressCheckoutButton = computed(() =>
  shouldRender.value
  && context === 'cart'
  && (showExpander || context === 'cart')
  && !isExpressCheckoutExpanded.value
  && !$viewport.md
  && !cart.primeShippingSelected
)

const baseClassesContainer = computed(() =>
  context === 'quickshop'
    ? [
        'grid gap-2',
        {
          'cols-2': isApplePayEnabled.value && isPaypalButtonEnabled.value,
          'mt-2': isApplePayEnabled.value || isPaypalReady.value
        }
      ]
    : [
        isShopPayEnabled.value ? 'gap-2' : 'gap-1',
        useWrappingLayout.value ? 'flex wrap' : 'grid'
      ]
)

const applePayLoading = computed(() =>
  (showApplePayLoading ?? context === 'cart') ? isApplePayActive.value : undefined
)

const handleApplePayCheckout = () => {
  try {
    if (context === 'quickshop' && selectedVariant) {
      $gtm.push('checkout.onLoadEventData', 'APPLE_PAY')
      initApplePaySession({
        productId: selectedVariant.id,
        upc: selectedVariant.upc
      })
      return
    }
    if (applePayPayload) {
      initApplePaySession(applePayPayload)
      return
    }
    initApplePaySession()
  }
  catch (err) {
    log.info(err)
  }
}

const openTermsAcceptanceDialog = () => {
  if (showApplePayTermsDialog) {
    DialogTermsAcceptance.open()
  }
  else {
    handleApplePayCheckout()
  }
}

const mountShopPayButton = async () => {
  if (!isShopPayEnabled.value || showExpressCheckoutButton.value || shopPayInitialized.value) return

  try {
    await nextTick()
    await initializeShopPay(`#${shopPayButtonId}`)
    shopPayInitialized.value = true
  }
  catch (err) {
    log.error(`Failed to initialize Shop Pay: ${err instanceof Error ? err.message : 'unknown error'}`)
  }
}

// Apple Pay library on chrome doesn't always load
const validateApplePayLoaded = async () => {
  let loadingCount = 0
  while (!window.ApplePaySession && loadingCount < 10) {
    await wait(500)
    loadingCount++
  }

  applePaySessionAvailable.value = !!window.ApplePaySession
}

onMounted(async () => {
  $deviceFingerPrint.load()
  if (!instance.value && isPaypalButtonEnabled.value)
    instance.value = await loadPaypal()

  await validateApplePayLoaded()
  await mountShopPayButton()
})

onBeforeUnmount(destroyShopPay)

watch([isShopPayEnabled, showExpressCheckoutButton], async ([enabled, isCollapsed]) => {
  if (enabled && !isCollapsed) {
    await mountShopPayButton()
    return
  }

  destroyShopPay()
  shopPayInitialized.value = false
  document.querySelector(`#${shopPayButtonId}`)?.replaceChildren()
})

watch(showExpressCheckoutButtonExpander, (value) => {
  isExpressCheckoutExpanded.value = !value
})
</script>
